import { Button, Flex, Stack, Text, Title } from '@mantine/core';
import { FormattedMessage } from 'react-intl';

import { AlertBanner } from '@shipstation/client-common/components/alert-banner';

import { ConnectionSyncTable } from './components/connection-sync-table/connection-sync-table';
import { SyncSummaryCard } from './components/sync-summary-card/sync-summary-card';
import { useProductConnectionSync } from './use-product-connection-sync';
import { messages } from './product-inventory-sync.messages';
import style from './product-inventory-sync-panel.module.css';

type ProductInventorySyncPanelProps = {
  productSku: string;
  onEditConfigurationClick: () => void;
};

export const ProductInventorySyncPanel = ({ productSku, onEditConfigurationClick }: ProductInventorySyncPanelProps) => {
  const { rows, summary, isLoading, hasLoadError, refetch } = useProductConnectionSync(productSku);

  if (!productSku) {
    return (
      <Stack className={style.panel}>
        <Text size='sm'>
          <FormattedMessage {...messages.noSkuMessage} />
        </Text>
      </Stack>
    );
  }

  const hasConnections = rows.length > 0;
  const hasEligibleTarget = rows.some(({ ineligibleReason }) => ineligibleReason === null);

  return (
    <Stack className={style.panel} gap='md'>
      {hasLoadError && (
        <AlertBanner description={<FormattedMessage {...messages.loadErrorMessage} />} status='alert'>
          <AlertBanner.Actions>
            <Button onClick={() => void refetch()} variant='secondary'>
              <FormattedMessage {...messages.loadErrorRetry} />
            </Button>
          </AlertBanner.Actions>
        </AlertBanner>
      )}
      <SyncSummaryCard isLoading={isLoading} onEditConfigurationClick={onEditConfigurationClick} summary={summary} />
      {!isLoading && !hasConnections && !hasLoadError ? (
        <Flex align='center' className={style['empty-state']} direction='column' gap='sm'>
          <Title order={4}>
            <FormattedMessage {...messages.notConfiguredTitle} />
          </Title>
          <Text size='sm' ta='center'>
            <FormattedMessage {...messages.notConfiguredDescription} />
          </Text>
          <Button onClick={onEditConfigurationClick} variant='primary'>
            <FormattedMessage {...messages.notConfiguredButton} />
          </Button>
        </Flex>
      ) : (
        <Stack gap='xs'>
          {!isLoading && hasConnections && !hasEligibleTarget && (
            <Text c='dimmed' size='sm'>
              <FormattedMessage {...messages.noTargetsNote} />
            </Text>
          )}
          <ConnectionSyncTable isLoading={isLoading} rows={rows} />
        </Stack>
      )}
    </Stack>
  );
};
